module Client.Menu

open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Style
open System
open Fable.Core.JsInterop
open Fable
open Fetch
open Shared
open Client.Pages
open Fable.Core
open Elmish.React

type Model = {
    SectionTitle: string;
    SuccessMessage: string;
    Menu: TreeNodeDTO option;
    OpenMenu: TreeNodeDTO option
    Tenant: string option
    Username: string
    Password: string
    Code: string
    ShowLogin: bool
    User: User option
    LoginError: string;
    ResetEmail: string
    ResetError: string
    ResetSent: bool
    ShowResetBar: bool
    MenuCollapse: bool
    ShowingLicenses: bool
    QRCode: option<string * string>
}

type Msg =
    | Logout
    | DismissSuccess
    | LoadedMenuData of string * TreeNodeDTO
    | Toggle of TreeNodeDTO
    | HideMenu
    | FetchError of exn
    | SwitchedTenant of string
    | ShowLoginOverlay
    | HideLoginOverlay
    | SetPassword of string
    | SetUsername of string
    | SetCode of string
    | SetResetEmail of string
    | DoLogin
    | LoginResult of LoginResponse
    | LoginSuccessOnServer of JWT
    | StoreLoginOnClient of JWT
    | LoginFailure of exn
    | StorageFailure of exn
    | ResetFailure of exn
    | ClickReset
    | ResetEmailSent
    | ShowReset
    | HideReset
    | ToggleMenuBurger
    | ShowLicenses
    | HideLicenses

let authUser login =
    promise {
        if String.IsNullOrEmpty login.UserName then return! failwithf "You need to fill in a username." else ()
        if String.IsNullOrEmpty login.Password then return! failwithf "You need to fill in a password." else ()

        return! FetchRequest.postPromise<Login,LoginResponse> Urls.Login None login
    }

let authUserCmd login = 
    Cmd.OfPromise.either authUser login LoginResult LoginFailure

let storeLoggedInUserCmd (newUser: JWT) = Cmd.OfFunc.either (BrowserLocalStorage.save "user") newUser (fun _ -> StoreLoginOnClient newUser) StorageFailure

let getPageHierarchy tenant =
    FetchRequest.postPromise<string, Domain.TreeNodeDTO> 
        Urls.GetPageHierarchy
        (Some 
            [
                RequestProperties.Cache RequestCache.Default
                Fetch.requestHeaders [
                    HttpRequestHeaders.ContentType "application/json"
                    HttpRequestHeaders.CacheControl "max-age=1800"
                ]
            ])
        tenant

let postGetPageHierarchyCmd tenant = 
    Cmd.OfPromise.either getPageHierarchy tenant (fun x -> LoadedMenuData(tenant, x)) FetchError

let resetEmail email =
    promise {
        if String.IsNullOrEmpty email then return! failwithf "You need to fill in your email address." else ()
        let props = 
            [ 
                Method HttpMethod.POST
                Fetch.requestHeaders [
                    ContentType "application/json" 
                ]
                Body !^email
            ]           

        try
            let! response = Fetch.fetch Urls.RequestResetEmail props
            
            if not response.Ok then
                return! failwithf "Error: %d" response.Status
            else    
                return true
        with
        | _ -> return! failwithf "Could not reset email."
    }


let dismissSuccessCmd() =
    Cmd.OfAsync.either (fun _ -> 
        async {
            do! Async.Sleep 10000 
            return 1 
        }) 0 (fun _ -> DismissSuccess) (fun _ -> DismissSuccess)

let init tenant withPages : Model * Cmd<Msg> = 
    { 
        SectionTitle = ""
        SuccessMessage = ""
        Menu = None
        OpenMenu = None
        Tenant = tenant
        ShowLogin = false
        Password = ""
        Username = ""
        Code = ""
        User = None
        LoginError = ""
        ResetEmail = ""
        ResetError = ""
        ResetSent = false
        ShowResetBar = false
        MenuCollapse = true
        ShowingLicenses = false
        QRCode = None
    }, match tenant with None -> [] | Some tenant -> if withPages then postGetPageHierarchyCmd tenant else []

let shouldSwitchTenant model tenantName =
    if (Some tenantName) <> model.Tenant then
        postGetPageHierarchyCmd tenantName
    else
        Cmd.none     

let update (msg:Msg) model : Model*Cmd<Msg> = 
    match msg with
    | Logout -> model, Cmd.none
    | DismissSuccess -> { model with SuccessMessage = "" }, (dismissSuccessCmd())
    | LoadedMenuData (tenant, menu) -> { model with Menu = Some menu; Tenant = Some tenant }, []
    | HideMenu ->
        { model with OpenMenu = None; MenuCollapse = true }, []
    | Toggle menu ->
        let opened =
            match model.OpenMenu, menu with
            | Some m1, m2 when m1 = m2 -> None
            | _,m -> Some m
        { model with OpenMenu = opened },[]
    | ToggleMenuBurger ->
        { model with MenuCollapse = not model.MenuCollapse}, []
    | SwitchedTenant newTenant -> model, postGetPageHierarchyCmd newTenant
    | FetchError err -> { model with SectionTitle = "Error fetching menu: " + err.ToString() },[]
    | ShowLoginOverlay -> { model with ShowLogin = true }, []
    | HideLoginOverlay -> { model with ShowLogin = false }, []
    | DoLogin ->
        { model with LoginError = ""; QRCode = None },
        authUserCmd { UserName = model.Username; Password = model.Password; PasswordId = Guid.Empty; ResetEmail = ""; Code = model.Code }  
    | LoginResult result ->
        match result with
        | SignIn token ->
            model, Cmd.ofMsg (LoginSuccessOnServer token)
        | LoginResponse.LoginFailure msg ->
            { model with LoginError = msg; }, []
        | LoginResponse.SetMFA (nonce, key) ->
            { model with QRCode = Some (nonce, QRC.getDataUrl key) }, []

    | LoginFailure exn -> { model with LoginError = exn.Message }, [] 
    | LoginSuccessOnServer jwt -> model, storeLoggedInUserCmd jwt
    | SetUsername username -> { model with Username = username }, [] 
    | SetCode code -> { model with Code = code }, [] 
    | SetPassword pw -> { model with Password = pw }, []   
    | SetResetEmail email -> { model with ResetEmail = email }, []   
    | ShowReset -> { model with ShowResetBar = true; ResetError = ""; ResetSent = false }, []
    | HideReset -> { model with ShowResetBar = false }, []
    | ClickReset -> { model with ResetSent = false; ResetError = "" }, (Cmd.OfPromise.either resetEmail (model.ResetEmail) (fun _ -> ResetEmailSent) ResetFailure)
    | ResetEmailSent -> { model with ResetSent = true; ResetEmail = "" }, Cmd.none
    | ResetFailure exn -> { model with ResetSent = true; ResetError = string (exn.Message) }, []
    | StoreLoginOnClient jwt -> 
        let adminPage = Page.Admin (TenantRef.get jwt.Account.Tenant, "")
        Browser.Dom.console.log("admin", adminPage)
        { model with ShowLogin = false },Elmish.Navigation.Navigation.newUrl (toHash adminPage)
    | StorageFailure exn -> { model with LoginError = "Error logging in: " + exn.Message }, [] 
    | ShowLicenses ->
        { model with ShowingLicenses = true }, []
    | HideLicenses ->
        { model with ShowingLicenses = false }, []

let renderInteractive tenant (openMenu: TreeNodeDTO option)  dispatch = 
    let loc = {Id=Slug "self-referral"; Name="Self Referral"}
    let node = TreeNodeDTO.Node (loc, [||])

    ul [ ClassName "nav navbar-nav navbar-right" ] [
        li [ ClassName ("dropdown" + match openMenu with Some menu when menu = node -> " open" | _ -> "") ] [
            if tenant <> "training" then
                yield a [ 
                    jsVoid
                    ClassName "dropdown-toggle"; 
                    Role "button" 
                    OnClick (fun _ -> dispatch (Toggle node))
                ] [
                    str (loc.Name)
                    span [ ClassName "carat" ] []
                ]
            match openMenu with
            | Some menu when menu = node && tenant <> "training" ->
                yield ul [ ClassName "dropdown-menu" ] [
                    yield li [] [ a [ Href (Pages.toHash (Pages.Page.AlcoholAudit tenant)) ] [ str "Alcohol Audit" ] ]
                    yield li [] [ a [ Href (Pages.toHash (Pages.Page.SelfReferral tenant)) ] [ str "Self-Referral Form" ] ]
                ]
            | _ -> ()
        ]
    ]

let renderMenu (model: Model) (root: TreeNodeDTO) (openMenu: TreeNodeDTO option) (dispatch: Msg -> unit) =
    div [ ClassName ("navbar-collapse collapse" + if model.MenuCollapse = false then " in" else "") ] [
        match model.Tenant with
        | None -> ()
        | Some tenant ->        
            for child in root.Children do
                yield ul [ ClassName "nav navbar-nav navbar-right" ] [
                    li [ ClassName ("dropdown" + match openMenu with Some menu when menu = child -> " open" | _ -> "") ] [
                        yield a [ 
                            jsVoid
                            ClassName "dropdown-toggle"; 
                            Data ("toggle", "dropdown"); 
                            Role "button" 
                            OnClick (fun _ -> dispatch (Toggle child))
                        ] [
                            str (child.Key.Name)
                            span [ ClassName "carat" ] []
                        ]
                        match openMenu with
                        | Some menu when menu = child ->
                            yield ul [ ClassName "dropdown-menu" ] [
                                yield li [] [ a [ Href (sprintf "#/areas/%s/pages/%s" (tenant) (Domain.Location.getId menu.Key)) ] [ str menu.Key.Name ] ]
                                yield li [ Role "separator"; ClassName "divider" ] []
                                for sub in child.Children do
                                    yield li [] [ a [ Href (sprintf "#/areas/%s/pages/%s.%s" (tenant) (Domain.Location.getId menu.Key) (Domain.Location.getId sub.Key)) ] [ str sub.Key.Name ] ]
                            ]
                        | _ -> ()
                    ]
                ]
            match model.Tenant with
            | None -> ()
            | Some tenant ->  yield renderInteractive tenant openMenu dispatch      
    ]

let renderLogin model dispatch =
    modal "Log in to your admin account" dispatch HideLoginOverlay [
        form [ Role "form" ] [
            div [ ClassName "form-group username" ] [
                label [ HtmlFor "usernameMC" ] [ str "Username" ]
                input [ 
                    Id "usernameMC"
                    HTMLAttr.Type "text"
                    ClassName "form-control input-lg"
                    Placeholder "Enter your username."
                    AutoFocus true
                    OnChange (fun ev -> dispatch (SetUsername !!ev.target?value))
                ]
                label [ HtmlFor "passwordMC" ] [ str "Password" ]
                input [ 
                    Id "passwordMC"
                    HTMLAttr.Type "password"
                    ClassName "form-control input-lg"
                    Placeholder "Enter your password."
                    Keyboard.onEnter DoLogin dispatch
                    OnChange (fun ev -> dispatch (SetPassword !!ev.target?value))
                ]
                label [ HtmlFor "codeMC" ] [ str "Auth Code" ]
                input [ 
                    Id "codeMC"
                    HTMLAttr.Type "text"
                    ClassName "form-control input-lg"
                    Placeholder "Enter your auth code."
                    Keyboard.onEnter DoLogin dispatch
                    OnChange (fun ev -> dispatch (SetCode !!ev.target?value))
                ]
                div [ 
                    ClassName "alert alert-danger"
                    Style [
                        MarginTop "10px"
                        Display (if String.IsNullOrEmpty model.LoginError then DisplayOptions.None else DisplayOptions.Block)
                    ]
                ] [ str model.LoginError ]

                div [] [
                    match model.QRCode with
                    | None -> ()
                    | Some (_,qr) ->
                        yield div [ ClassName "alert alert-warning" ] [
                            p [] [ str "The system requires application-based Multi-Factor Authentication." ]
                            p [] [
                                str "Please install an application, e.g. Google Authenticator on a device (such as a mobile phone) "
                                str "and scan this QR Code. Use this app go generate an Auth Code."
                            ]
                            div [ Style [ TextAlign TextAlignOptions.Center ] ] [
                                img [ Src qr ]
                            ]
                        ]
                ]

                a [ 
                    Href "javascript:void(0)" 
                    OnClick (fun _ -> dispatch (if model.ShowResetBar then HideReset else ShowReset))
                ] [ str "I've forgotten my password" ]
                div [
                    Id "passwordReset"
                    Style [
                        Visibility (if model.ShowResetBar then "visible" else "hidden")
                        CSSProp.Position (if model.ShowResetBar then PositionOptions.Relative else PositionOptions.Absolute)
                    ]
                    ClassName (if model.ShowResetBar then "fade in" else "fade")
                ] [
                    inlineForm [
                        yield formGroup [
                            label [
                                ClassName "sr-only"
                                HtmlFor "resetEmail"
                            ] [ str "Enter email" ]
                            input [ 
                                Id "resetEmail"
                                HTMLAttr.Type "text"
                                ClassName "form-control"
                                Placeholder "Enter your email address."
                                Style [
                                    Width "300px"
                                ]
                                OnChange (fun ev -> dispatch (SetResetEmail !!ev.target?value))
                                Helpers.valueOrDefault model.ResetEmail

                            ]
                            button [ 
                                HTMLAttr.Type "button"
                                HTMLAttr.Disabled (String.IsNullOrEmpty model.ResetEmail)
                                HTMLAttr.Value model.ResetEmail
                                ClassName "btn btn-default"
                                Keyboard.onEnter ClickReset dispatch
                                OnClick (fun _ -> dispatch ClickReset)
                            ] [ str "Request reset" ]
                        ]
                        if String.IsNullOrWhiteSpace model.ResetError = false || model.ResetSent then
                            yield p [ ClassName "alert alert-success" ] [ str (if model.ResetSent then "Your reset request has been sent" else model.ResetError) ]
                    ]
                ]
            ]
        ]
    ] [
        button [
            HTMLAttr.Type "button"
            HTMLAttr.Disabled (model.Username.Length = 0 || model.Password.Length = 0)
            ClassName "btn btn-primary"
            Keyboard.onEnter DoLogin dispatch
            OnClick (fun _ -> dispatch DoLogin)
        ] [ str "Log In" ]
        button [ 
            ClassName "btn btn-default" 
            HTMLAttr.Type "button"
            OnClick (fun _ -> dispatch HideLoginOverlay)
        ] [ str "Close" ]
    ]

let view (model:Model) dispatch contents =
    [
        yield nav [ 
            ClassName "navbar navbar-default navbar-inverse clearfix"
            Role "navigation"
        ] [
            fluidContainer [
                yield div [ ClassName "navbar-header" ] [
                    // creates the collapsed display spans
                    button [ 
                        ClassName ("navbar-toggle" + if model.MenuCollapse then " collapsed" else "")
                        OnClick (fun ev -> dispatch ToggleMenuBurger)
                    ] [
                        match model.Menu with
                        | None -> ()
                        | Some menu -> 
                            yield span [ ClassName "sr-only" ] [ str "Show Menu"]
                            match model.Tenant with
                            | None -> ()
                            | Some _ ->        
                                for _ in menu.Children do yield span [ ClassName "icon-bar" ] []
                        
                    ]
                    a [ 
                        ClassName "navbar-brand" 
                        Style [
                            WhiteSpace WhiteSpaceOptions.Nowrap
                            MaxWidth "85vw"
                            TextOverflow "ellipsis"
                            CSSProp.Overflow  OverflowOptions.Hidden
                        ]
                        Href <| toHash Page.Home
                    ] [
                        img [ Src "images/logo.png"; Alt "Change Your Tomorrow" ]
                        str (sprintf "%s%s" (match model.Tenant with None -> "" | Some t -> t) (match model.SectionTitle with "" -> "" | t -> " - " + t))
                    ]
                ]
                match model.Menu with
                | None -> ()
                | Some menu -> yield renderMenu model menu model.OpenMenu dispatch
            ]  
        ]
        if String.IsNullOrEmpty model.SuccessMessage = false then
            yield div [
                ClassName "alert alert-success"
                Role "alert"
                OnClick (fun _ -> dispatch DismissSuccess)
                Style [
                    MarginBottom "0"
                ]
            ] [
                str model.SuccessMessage
            ]
        yield div [ 
            Id "main"
            ClassName "container-fluid expanding-child expanding-parent no-pad-lr"
        ] contents

        yield siteFooter (Some (fun _ -> dispatch ShowLoginOverlay)) (fun _ -> dispatch ShowLicenses)

        if model.ShowLogin then
            yield! renderLogin model dispatch
        if model.ShowingLicenses then
            yield! Client.Style.renderAttribution dispatch HideLicenses
    ]
   